import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import loginLeafMark from "../../assets/images/loginLeafMark.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CURRENCY_SYMBOL } from "../../constants/currency";
import { useTranslation } from "react-i18next";
import dashboardServices from "../../services/DashboardServices";
import saleOrderServices from "../../services/SalesOrderService";
import { OrderData } from "../../types/saleOrder";
import TopUpWalletModal from "./invoice&payment/TopUpWalletModal";
import { CustomerDashboardResponse, WalletType } from "../../types/dashboardType";
import PaymentStatusModal from "./invoice&payment/PaymentStatusModal";
import { onPaymentsEvent } from "../../services/paymentsEvents";
import { isUnpaidHeldCheckoutOrder } from "../../utils/checkoutSession";
import {
  formatWalletDisplay,
  normalizeBalanceSummaryResponse,
  normalizeDashboardResponse,
} from "../../helper/formatAppBalance";
import { useWalletBalanceVisibility } from "../../hooks/useWalletBalanceVisibility";
import { NJIM } from "../../theme/njimTheme";
import PageHeader from "../../components/ui/PageHeader";

export type DashboardProps = CustomerDashboardResponse;


const monoFont = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

const panelSx = {
  bgcolor: "rgba(255,255,255,0.82)",
  backdropFilter: "blur(20px) saturate(120%)",
  WebkitBackdropFilter: "blur(20px) saturate(120%)",
  borderRadius: "12px",
  boxShadow: NJIM.elevation,
  border: "none",
} as const;

const WalletLeaf = ({
  width,
  height,
  bottom,
  top,
  insetInlineEnd,
  insetInlineStart,
  opacity,
  rotate,
}: {
  width: number;
  height: number;
  bottom?: number;
  top?: number;
  insetInlineEnd?: number;
  insetInlineStart?: number;
  opacity: number;
  rotate: number;
}) => (
  <Box
    component="img"
    src={loginLeafMark}
    alt=""
    aria-hidden
    sx={{
      position: "absolute",
      width,
      height,
      bottom,
      top,
      ...(insetInlineEnd !== undefined ? { insetInlineEnd } : {}),
      ...(insetInlineStart !== undefined ? { insetInlineStart } : {}),
      opacity,
      transform: `rotate(${rotate}deg)`,
      filter: "grayscale(1) invert(1)",
      mixBlendMode: "screen",
      objectFit: "contain",
      pointerEvents: "none",
      userSelect: "none",
    }}
  />
);

type StatusTone = "neutral" | "info" | "success" | "warning";

const toneStyles: Record<
  StatusTone,
  { color: string; bgcolor: string; dot: string }
> = {
  neutral: { color: NJIM.textSecondary, bgcolor: NJIM.muted, dot: NJIM.textSecondary },
  info: { color: NJIM.info, bgcolor: "rgba(29,67,86,0.1)", dot: NJIM.info },
  success: { color: NJIM.success, bgcolor: "rgba(22,163,74,0.1)", dot: NJIM.success },
  warning: { color: NJIM.warning, bgcolor: "rgba(229,162,58,0.12)", dot: NJIM.warning },
};

const StatusBadge = ({
  tone,
  children,
}: {
  tone: StatusTone;
  children: React.ReactNode;
}) => {
  const styles = toneStyles[tone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        borderRadius: "999px",
        px: 1.25,
        py: 0.5,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
        color: styles.color,
        bgcolor: styles.bgcolor,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: styles.dot,
          flexShrink: 0,
        }}
      />
      {children}
    </Box>
  );
};

const StatCard = ({
  label,
  value,
  icon,
  tone,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  tone: StatusTone;
  hint?: string;
}) => {
  const chip = toneStyles[tone];
  return (
    <Box
      sx={{
        ...panelSx,
        p: { xs: 2, sm: 2.25 },
        display: "flex",
        flexDirection: "column",
        gap: 1,
        transition: "box-shadow 180ms ease",
        "&:hover": {
          boxShadow: NJIM.elevationLg,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
        <Typography sx={{ fontSize: 14, color: NJIM.textSecondary }}>{label}</Typography>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: chip.bgcolor,
            color: chip.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography
        sx={{
          fontFamily: monoFont,
          fontSize: { xs: 24, sm: 26 },
          fontWeight: 500,
          color: NJIM.textPrimary,
          lineHeight: 1,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>{hint}</Typography>
      )}
    </Box>
  );
};

const summaryGridSx = {
  alignItems: "start",
} as const;

const actionGridSx = {
  width: "100%",
} as const;

const SectionCard = ({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box sx={{ ...panelSx, overflow: "hidden", position: "relative", isolation: "isolate" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 2.5, sm: 3 },
        pt: { xs: 2.5, sm: 3 },
        pb: 2,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: NJIM.inkSoft,
          color: NJIM.ink,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 600, fontSize: 16, color: NJIM.textPrimary, lineHeight: 1.3 }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: { xs: 2.5, sm: 3 }, pb: { xs: 2.5, sm: 3 } }}>{children}</Box>
  </Box>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");
  const [dashboardData, setDashboardData] = useState<DashboardProps>({
    appWalletBalance: 0,
    currency: "IQD",
    recentOrders: 0,
    invoicesDue: 0,
  });
  const [walletBalance, setWalletBalance] = useState<WalletType>({
    balance: 0,
    customerCode: "",
    title: "",
    customerId: "",
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [recentOrders, setRecentOrders] = useState<OrderData[]>([]);
  const [topUpModalOpen, setTopUpModalOpen] = useState<boolean>(false);
  const [paymenetStatusModalOpen, setPaymentStatusModalOpen] = useState(false);
  const { balanceVisible, toggleBalanceVisible } = useWalletBalanceVisibility();
  const hasFetched = useRef(false);
  const [searchParams] = useSearchParams();

  const fetchDashboardMetrics = useCallback(async () => {
    try {
      const response = await dashboardServices.getDashboardData();
      const metrics = normalizeDashboardResponse(response?.data);
      setDashboardData((prev) => ({ ...prev, ...metrics }));
    } catch (error) {
      console.error("Error fetching dashboard metrics:", error);
    }
  }, []);

  /** Internal app balances only — never Sage totalOutstanding / wallet top-ups mixed in. */
  const fetchAppBalances = useCallback(async () => {
    try {
      const response = await dashboardServices.getBalanceSummary();
      const balances = normalizeBalanceSummaryResponse(response?.data);
      setDashboardData((prev) => ({ ...prev, ...balances }));
      setWalletBalance((prev) => ({
        ...prev,
        balance: balances.appWalletBalance,
      }));
    } catch (error) {
      console.error("Error fetching balance summary:", error);
    }
  }, []);

  const fetchWalletDetails = useCallback(async () => {
    try {
      const response = await dashboardServices.getWalletBalance();
      const walletData = response?.data;
      if (walletData) {
        setWalletBalance((prev) => ({
          ...walletData,
          balance: prev.balance ?? walletData.balance,
        }));
      }
    } catch (error) {
      console.error("Error fetching wallet details:", error);
    }
  }, []);

  const refreshBalances = useCallback(async () => {
    await Promise.all([
      fetchAppBalances(),
      fetchWalletDetails(),
      fetchDashboardMetrics(),
    ]);
  }, [fetchAppBalances, fetchWalletDetails, fetchDashboardMetrics]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchOrderList();
    refreshBalances();
  }, [refreshBalances]);

  useEffect(() => {
    return onPaymentsEvent(() => {
      refreshBalances();
    });
  }, [refreshBalances]);

  useEffect(() => {
    const type = searchParams.get("type");

    if (type) {
      setPaymentStatusModalOpen(true);
    }
  }, [searchParams]);

  const fetchOrderList = async () => {
    try {
      setLoading(true);
      const params = {
        keyword: "",
        pageNumber: 1,
        pageSize: 5,
        sortDescending: true,
      };
      const response = await saleOrderServices.getSaleOrderList(params);
      const finalResponse = response?.data?.items;
      if (finalResponse && finalResponse.length > 0) {
        setRecentOrders(
          finalResponse.filter((order: OrderData) => {
            if (!isUnpaidHeldCheckoutOrder(order.id)) return true;
            const status = String(order.paymentStatus ?? "").toLowerCase();
            return Boolean(order.isSynced) || status.includes("paid") || status.includes("success");
          })
        );
      }
    } catch (error) {
      console.error("Error fetching Banner Image:", error);
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedStatus = (status: string | number | undefined) => {
    const statusValue = String(status ?? "").trim();
    if (!statusValue) return "-";

    const normalizedStatus = statusValue
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]/g, " ")
      .toLowerCase()
      .replace(/\s+/g, "");

    const fallbackStatus = statusValue.replace(/([a-z])([A-Z])/g, "$1 $2");
    return t(`status.${normalizedStatus}`, { defaultValue: fallbackStatus });
  };

  const getStatusTone = (status: string | number | undefined): StatusTone => {
    const statusValue = String(status ?? "").toLowerCase();
    if (statusValue === "delivered") return "success";
    if (statusValue === "shipped") return "info";
    if (statusValue === "confirmed" || statusValue === "pending") return "warning";
    return "neutral";
  };

  const formatOrderAmount = (order: OrderData) => {
    const amount = order.total ?? order.amount ?? 0;
    if (typeof amount === "number") {
      return amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return amount;
  };

  const lastOrderDate = recentOrders[0]?.date
    ? new Date(recentOrders[0].date).toISOString().split("T")[0]
    : undefined;

  const walletDisplayAmount =
    dashboardData.appWalletBalance ?? walletBalance.balance;

  const maskedBalanceDisplay = useMemo(() => {
    const prefix = dashboardData.currency?.trim()
      ? `${dashboardData.currency.trim()} `
      : `${CURRENCY_SYMBOL.trim()} `;
    return `${prefix}••••••••`;
  }, [dashboardData.currency]);

  return (
    <>
      <Box className="dashboard-page" sx={{ direction: isRTL ? "rtl" : "ltr" }}>
        <Box className="dashboard-section">
          <PageHeader
            title={t("dashboard.title")}
            subtitle={t("dashboard.welcomeBack")}
          />
        </Box>

        {/* Summary row — wallet + 2 stat cards */}
        <Box className="dashboard-section dashboard-summary-grid" sx={summaryGridSx}>
          <Box
            sx={{
              position: "relative",
              p: 2,
              borderRadius: "12px",
              overflow: "hidden",
              color: NJIM.white,
              background: NJIM.walletHero,
              minWidth: 0,
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              isolation: "isolate",
            }}
          >
            <Box sx={{ pointerEvents: "none", position: "absolute", inset: 0, overflow: "hidden" }}>
              <Box
                sx={{
                  position: "absolute",
                  width: 220,
                  height: 220,
                  borderRadius: "50%",
                  top: -80,
                  insetInlineEnd: -60,
                  bgcolor: NJIM.mint,
                  filter: "blur(60px)",
                  opacity: 0.3,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  width: 180,
                  height: 180,
                  borderRadius: "50%",
                  bottom: -70,
                  insetInlineStart: -50,
                  bgcolor: NJIM.mint,
                  filter: "blur(55px)",
                  opacity: 0.22,
                }}
              />
              <WalletLeaf width={340} height={340} bottom={-90} insetInlineEnd={-80} opacity={0.22} rotate={-8} />
              <WalletLeaf width={160} height={160} top={-40} insetInlineStart={-40} opacity={0.1} rotate={16} />
            </Box>

            <Box sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: NJIM.mintLight,
                  }}
                >
                  <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 14 }} />
                  {t("dashboard.prepaymentWallet")}
                </Typography>
                <Box
                  sx={{
                    mt: 0.75,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 0,
                    maxWidth: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: monoFont,
                      fontSize: { xs: 15, sm: 18, md: 20 },
                      fontWeight: 500,
                      color: NJIM.white,
                      lineHeight: 1.2,
                      letterSpacing: "-0.02em",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {balanceVisible
                      ? formatWalletDisplay(walletDisplayAmount, dashboardData.currency)
                      : maskedBalanceDisplay}
                  </Typography>
                  <Tooltip
                    title={
                      balanceVisible
                        ? t("walletPage.hideBalance")
                        : t("walletPage.showBalance")
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={toggleBalanceVisible}
                      aria-label={
                        balanceVisible
                          ? t("walletPage.hideBalance")
                          : t("walletPage.showBalance")
                      }
                      sx={{
                        flexShrink: 0,
                        color: "rgba(255,255,255,0.65)",
                        "&:hover": {
                          color: NJIM.white,
                          bgcolor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      {balanceVisible ? (
                        <VisibilityOffIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <VisibilityIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.55)", mt: 0.25 }}>
                  {t("dashboard.availableForOrders")}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap", minWidth: 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  className="njim-accent-btn"
                  sx={{
                    flex: "1 1 120px",
                    minWidth: 0,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    py: 0.65,
                    gap: 0.5,
                    "& .MuiButton-startIcon, & .MuiButton-endIcon": { margin: 0 },
                  }}
                  {...(isRTL ? { endIcon: <AddIcon sx={{ fontSize: 16 }} /> } : { startIcon: <AddIcon sx={{ fontSize: 16 }} /> })}
                  onClick={() => setTopUpModalOpen(true)}
                >
                  {t("dashboard.topUp")}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  className="njim-secondary-btn--dark"
                  sx={{
                    flex: "1 1 120px",
                    minWidth: 0,
                    fontSize: 12,
                    py: 0.65,
                    gap: 0.5,
                    "& .MuiButton-startIcon, & .MuiButton-endIcon": { margin: 0 },
                  }}
                  {...(isRTL ? { endIcon: <DescriptionOutlinedIcon sx={{ fontSize: 16 }} /> } : { startIcon: <DescriptionOutlinedIcon sx={{ fontSize: 16 }} /> })}
                  onClick={() => navigate("/walletBalance")}
                >
                  {t("dashboard.viewStatement")}
                </Button>
              </Box>
            </Box>
          </Box>

        <StatCard
          label={t("dashboard.recentOrders")}
          value={dashboardData.recentOrders}
          tone="info"
          hint={
            lastOrderDate
              ? `${t("dashboard.lastOrder")} ${lastOrderDate}`
              : undefined
          }
          icon={<Inventory2OutlinedIcon sx={{ fontSize: 18 }} />}
        />

        <StatCard
          label={t("dashboard.invoicesDue")}
          value={dashboardData.invoicesDue}
          tone="warning"
          hint={t("dashboard.requiresPayment")}
          icon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
        />
        </Box>

        {/* Quick Actions */}
        <Box className="dashboard-section dashboard-after-summary">
          <SectionCard
            title={t("dashboard.quickActions")}
            icon={<AddIcon sx={{ fontSize: 18 }} />}
          >
            <Box className="dashboard-action-grid" sx={actionGridSx}>
              <Button
                fullWidth
                variant="contained"
                className="njim-primary-btn"
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "start",
                  height: "auto",
                  py: { xs: 1.5, sm: 2 },
                  px: 2,
                  gap: 1,
                  color: `${NJIM.white} !important`,
                  "& .MuiTypography-root": { color: NJIM.white },
                }}
                {...(isRTL ? { endIcon: <AddIcon sx={{ fontSize: 18, flexShrink: 0 }} /> } : { startIcon: <AddIcon sx={{ fontSize: 18, flexShrink: 0 }} /> })}
                onClick={() => navigate("/saleOrder")}
              >
                <Box sx={{ textAlign: "start", minWidth: 0 }}>
                  <Typography sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 600, lineHeight: 1.3, color: NJIM.white }}>
                    {t("dashboard.createNewOrder")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: NJIM.white,
                      opacity: 0.8,
                      display: { xs: "none", sm: "block" },
                      lineHeight: 1.3,
                    }}
                  >
                    {t("dashboard.startNewOrder")}
                  </Typography>
                </Box>
              </Button>
              <Button
                fullWidth
                variant="outlined"
                className="njim-secondary-btn"
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "start",
                  height: "auto",
                  py: { xs: 1.5, sm: 2 },
                  px: 2,
                  gap: 1,
                }}
                {...(isRTL ? { endIcon: <DescriptionOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} /> } : { startIcon: <DescriptionOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} /> })}
                onClick={() => navigate("/invoicesPayments")}
              >
                <Box sx={{ textAlign: "start", minWidth: 0 }}>
                  <Typography sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 600, lineHeight: 1.3 }}>
                    {t("dashboard.viewInvoices")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: NJIM.textSecondary,
                      display: { xs: "none", sm: "block" },
                      lineHeight: 1.3,
                    }}
                  >
                    {t("dashboard.managePendingInvoices")}
                  </Typography>
                </Box>
              </Button>
            </Box>
          </SectionCard>
        </Box>

        {/* Recent Orders Summary */}
        <Box className="dashboard-section">
          <SectionCard
            title={t("dashboard.recentOrdersSummary")}
            icon={<Inventory2OutlinedIcon sx={{ fontSize: 18 }} />}
          >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={44} sx={{ color: NJIM.ink }} />
          </Box>
        ) : recentOrders.length === 0 ? (
          <Typography
            sx={{ textAlign: "center", color: NJIM.textSecondary, py: 5, fontSize: 14 }}
          >
            {t("common.noData") || "No recent orders"}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {recentOrders.map((order) => (
              <Box
                key={order.id}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  gap: { xs: 1.5, sm: 2 },
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: "12px",
                  bgcolor: "rgba(243,247,251,0.5)",
                  transition: "background-color 180ms ease",
                  "&:hover": { bgcolor: NJIM.muted },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: monoFont,
                      fontWeight: 500,
                      fontSize: { xs: 14, sm: 15 },
                      color: NJIM.textPrimary,
                    }}
                  >
                    {order.number}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: NJIM.textSecondary, mt: 0.25 }}>
                    <Box component="span" sx={{ fontFamily: monoFont }}>
                      {order.date
                        ? new Date(order.date).toISOString().split("T")[0]
                        : "-"}
                    </Box>
                    {" • "}
                    {order.items?.length || 0} {t("common.items")}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "space-between", sm: "flex-end" },
                    gap: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: monoFont,
                      fontWeight: 500,
                      fontSize: { xs: 14, sm: 15 },
                      color: NJIM.textPrimary,
                    }}
                  >
                    {CURRENCY_SYMBOL} {formatOrderAmount(order)}
                  </Typography>
                  <StatusBadge tone={getStatusTone(order.status)}>
                    {getLocalizedStatus(order.status)}
                  </StatusBadge>
                </Box>
              </Box>
            ))}
          </Box>
        )}
          </SectionCard>
        </Box>
      </Box>

      <TopUpWalletModal
        open={topUpModalOpen}
        onClose={() => {
          setTopUpModalOpen(false);
          refreshBalances();
        }}
        WalletData={walletBalance}
      />
      <PaymentStatusModal
        open={paymenetStatusModalOpen}
        onClose={() => {
          setPaymentStatusModalOpen(false);
          refreshBalances();
          navigate("/dashboard", { replace: true });
        }}
      />
    </>
  );
};

export default Dashboard;
